import React, { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';

import { required } from '@origin-digital/validation';
import { IProperty, IAddress } from '@origin-digital/shared-app-context';
import { reportException, logger } from '@origin-digital/reporting-client';
import {
  IContractAccountStatus,
  IContractAccountBalancesDTO,
  IContractAccountInvoice,
} from '@origin-digital/payment-service-dapi-contract';
import {
  Button,
  Card,
  CardStackSection,
  Heading,
  Stack,
  Text,
  TextField,
} from '@origin-digital/ods-core';

import { useAppContext } from 'src/context/AppContext';
import { useSharedAppContext } from 'src/context/SharedAppContext';
import { useTextFieldState } from 'src/hooks';
import { divisionCodeMap, IDivisionIdWithLpg, invalidRedirectParam, routes } from 'src/consts';
import {
  IContractAccountBalanceWithSacInfo,
  ILPGRetrievedAccounts,
  AccountBackend,
} from 'src/interfaces';
import { Form } from 'src/components/Form';
import { Loading } from 'src/components/Loading';
import { AccountSelector } from 'src/components/AccountSelector';
import {
  isKrakenId,
  reduceValidators,
  isMyAccount,
  hasValidAmount,
  getSalesforceContractObject,
  formatValidator,
} from 'src/utils/helpers';
import {
  hideNotification,
  showErrorNotification,
  showInfoNotification,
} from 'src/utils/notification';
import { formStart, formUpdate } from 'src/utils/tracking';
import { verifyContractAccount, getAccountBalances, getLPGCustomerData } from 'src/services';

import { OtherPaymentCard } from './OtherPaymentCard';
import { transformAccountId } from './utils';

import * as content from './content.json';

export const SelectContractAccountScreen: React.FC = () => {
  const { context, setContext } = useAppContext();
  const sharedAppContext = useSharedAppContext();
  const [mounting, setMounting] = useState(isMyAccount());
  const [redirecting, setRedirecting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [accountBalances, setAccountBalances] = useState<IContractAccountBalanceWithSacInfo[]>([]);
  const [authAmount, setAuthAmount] = useState<number>(0);

  const contractAccountState = useTextFieldState(
    transformAccountId(context.contractAccountId || ''),
    content.form.fields.contractAccountId.helpText,
  );

  const getErrorMessage = (): string => {
    const { errors } = content.form.fields.contractAccountId;
    const { value } = contractAccountState;

    const validators = [required({ error: errors.required }), formatValidator(value, errors)];

    return reduceValidators(contractAccountState.value, validators);
  };

  const handleValidate = (): string => {
    const errorMessage = getErrorMessage();

    contractAccountState.setError(errorMessage);

    return errorMessage;
  };

  const hasFormErrors = (): boolean => {
    return !!handleValidate();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    formStart({ context, setContext });

    contractAccountState.setValue(transformAccountId(event.target.value));
  };

  const handleAccountSelection = (contractAccountId: string, amount: number) => {
    contractAccountState.setValue(contractAccountId);
    setAuthAmount(amount);
  };

  const handleFailedSubmit = (
    errorNotification: string,
    errorFunction: string,
    error: Error,
    isInfo?: boolean,
  ) => {
    setValidating(false);
    if (isInfo) {
      showInfoNotification(errorNotification);
    } else {
      showErrorNotification(errorNotification);
    }
    reportException(errorFunction, error);
  };

  const handleSubmit = async () => {
    try {
      let divisionId: IDivisionIdWithLpg | '';
      const accountId = contractAccountState.value;
      const isKrakenAccountNumber = isKrakenId(accountId);
      const contractAccountId = isKrakenAccountNumber
        ? accountId
        : accountId.replace(/[^0-9]/g, '');

      if (!isMyAccount()) {
        if (isKrakenAccountNumber) {
          setContext({
            contractAccountId,
            backend: AccountBackend.kraken,
          });
          setRedirecting(true);
          return;
        }

        const { fuelType, verified, status } = await verifyContractAccount({ contractAccountId });
        divisionId = fuelType as IDivisionIdWithLpg;

        if (!verified) {
          handleFailedSubmit(
            content.form.errors.invalid,
            'selectContractAccountScreen.handleSubmit()',
            Error('INVALID'),
          );
          return;
        }

        if (status === IContractAccountStatus.SETUP) {
          handleFailedSubmit(
            content.form.errors.onboarding,
            'selectContractAccountScreen.handleSubmit()',
            Error('ONBOARDING'),
            true,
          );
          return;
        }
      } else {
        const { divisionCode, type } = accountBalances.find(
          (account) => account.contractAccountId === contractAccountId,
        );
        divisionId = divisionCodeMap[divisionCode ? divisionCode : type];
      }

      formUpdate({
        divisionId,
      });

      const showAmountDue = hasValidAmount(authAmount.toString());
      const amountDue = String(authAmount / 100);

      setContext({
        divisionId,
        contractAccountId,
        showAmountDue,
        payAmountDue: showAmountDue,
        amount: showAmountDue ? amountDue : '',
        amountDue,
        backend: AccountBackend.sap,
      });

      setRedirecting(true);
    } catch (error) {
      handleFailedSubmit(
        content.form.errors.generic,
        'selectContractAccountScreen.handleSubmit()',
        error,
      );
    }
  };

  const submitForm = () => {
    formStart({ context, setContext });

    if (hasFormErrors()) {
      if (isMyAccount()) {
        showErrorNotification(content.form.errors.contractAccount);
      }
      return;
    }

    setValidating(true);
    hideNotification();
    handleSubmit();
  };

  const addSacInformationToAccountBalance = (
    contractAccountList: IContractAccountBalancesDTO,
    property: IProperty,
  ): IContractAccountBalanceWithSacInfo[] =>
    contractAccountList.contractAccounts.map((invoice) => {
      const { divisionCode, status } = property.contractAccounts.find(
        ({ contractAccountId }) => contractAccountId === invoice.contractAccountId,
      );
      return { ...invoice, divisionCode, status };
    });

  const addSacInformationToAccountBalanceV2 = (
    contractAccountList: IContractAccountInvoice[],
    retreivedAccountList: ILPGRetrievedAccounts[],
    address: IAddress,
  ): IContractAccountBalanceWithSacInfo[] => {
    let contractMapData = [];
    let retreivedAccountMapData = [];
    if (contractAccountList && contractAccountList.length > 0) {
      contractMapData = contractAccountList.map((invoice) => {
        const { type, status } = address.services.find(
          ({ backendId }) => backendId === invoice.contractAccountId,
        );
        return { ...invoice, type, status };
      });
    }
    if (retreivedAccountList && retreivedAccountList.length > 0) {
      retreivedAccountMapData = retreivedAccountList.map((account) => {
        const { type, status } = address.services.find(
          ({ backendId }) => backendId === account.CustomerNumber,
        );
        const invoice: IContractAccountInvoice = getSalesforceContractObject(
          account.CustomerNumber,
          account.AccountBalance * 100,
        );
        return { ...invoice, type, status };
      });
    }

    return contractMapData.concat(retreivedAccountMapData);
  };

  const loadAccountsFromProperties = async () => {
    const { premiseId, properties } = sharedAppContext;
    const currentProperty = properties.find((property) => property.premiseId === premiseId);
    const contractAccountIdList = currentProperty.contractAccounts.map(
      ({ contractAccountId }) => contractAccountId,
    );

    try {
      const response = await getAccountBalances({
        contractAccountIds: contractAccountIdList.join(),
      });
      const propertyAccountBalances = addSacInformationToAccountBalance(response, currentProperty);

      setAccountBalances(propertyAccountBalances);
      setMounting(false);
    } catch (error) {
      logger.warn('getAccountBalances()', error);
      throw Error(error);
    }
  };

  const loadAccountsFromAddresses = async () => {
    const { selectedAddressId, addresses } = sharedAppContext;
    const currentAddress = addresses.find((address) => address.id === selectedAddressId);
    const sapServicesList = currentAddress.services
      .filter((service) => service.type !== 'LPG')
      .map((service) => service.backendId);
    const salesforceServicesList = currentAddress.services
      .filter((service) => service.type === 'LPG')
      .map((service) => service.backendId);
    try {
      let sapResponse: IContractAccountBalancesDTO, sfResponse: ILPGRetrievedAccounts[];
      // sap based fuels account balance
      if (sapServicesList && sapServicesList.length > 0) {
        sapResponse = await getAccountBalances({ contractAccountIds: sapServicesList.join() });
      }
      // salesforce fuels account balance
      if (salesforceServicesList && salesforceServicesList.length > 0) {
        const response = await getLPGCustomerData({ custNumberList: salesforceServicesList[0] });
        // The API response we get back is in PascelCase but the generated API returns it in camelCase
        // due to the way it generates the API model. Due to this we need to manually set the response type
        sfResponse = (response.retreivedAccounts as unknown) as ILPGRetrievedAccounts[];
      }

      const addressAccountBalances = addSacInformationToAccountBalanceV2(
        sapResponse.contractAccounts,
        sfResponse,
        currentAddress,
      );
      setAccountBalances(addressAccountBalances);
      setMounting(false);
    } catch (error) {
      logger.warn('getAccountBalances()', error);
      throw Error(error);
    }
  };

  if (isMyAccount() && !accountBalances.length) {
    if (sharedAppContext.addresses) {
      loadAccountsFromAddresses();
    } else if (sharedAppContext.properties) {
      loadAccountsFromProperties();
    }
  }

  useEffect(
    () => {
      if (isMyAccount()) {
        setMounting(true);
        if (sharedAppContext.addresses && sharedAppContext.addresses.length > 0) {
          loadAccountsFromAddresses();
        } else if (sharedAppContext.properties && sharedAppContext.properties.length > 0) {
          loadAccountsFromProperties();
        }
      }
    },
    [sharedAppContext.premiseId],
  );

  if (mounting) {
    return <Loading />;
  }

  if (redirecting) {
    return (
      <div data-id="redirect-select-payment-method">
        <Redirect
          to={`${routes.selectPaymentMethod}?${invalidRedirectParam}=${encodeURIComponent(
            `?caid=${context.contractAccountId}`,
          )}`}
          push
        />
      </div>
    );
  }

  return (
    <CardStackSection data-id="select-contract-account">
      <Card data-id="select-contract-account-card">
        <Form onSubmit={submitForm}>
          <Stack space={['medium', 'large']}>
            {isMyAccount() ? (
              <Stack space={['medium', 'large']}>
                <Heading variant="h3">{content.heading.selectAccount}</Heading>

                {accountBalances.map((accountBalance) => (
                  <AccountSelector
                    key={accountBalance.contractAccountId}
                    isSelected={contractAccountState.value === accountBalance.contractAccountId}
                    contractAccountBalance={accountBalance}
                    onClick={handleAccountSelection}
                    disabled={validating}
                  />
                ))}
              </Stack>
            ) : (
              <Stack space={['medium', 'large']}>
                <Heading variant="h3">{content.heading.enterAccount}</Heading>

                <Text>{content.body}</Text>

                <TextField
                  id="contractAccountId"
                  name="contractAccountId"
                  label={content.form.fields.contractAccountId.label}
                  helperText={contractAccountState.helperText}
                  value={contractAccountState.value}
                  error={contractAccountState.error}
                  maxLength={15}
                  onBlur={() => {
                    contractAccountState.onBlur();
                    handleValidate();
                  }}
                  onFocus={contractAccountState.onFocus}
                  onChange={handleChange}
                  endIcon={
                    contractAccountState.value &&
                    !contractAccountState.focus &&
                    !contractAccountState.error
                      ? 'success'
                      : false
                  }
                />
              </Stack>
            )}

            <Button data-id="continue-button" spinner={validating} type="submit" fullWidth>
              {content.form.button}
            </Button>
          </Stack>
        </Form>
      </Card>
      {!isMyAccount() ? <OtherPaymentCard /> : null}
    </CardStackSection>
  );
};
